import { DateTime } from "luxon";
import {
  AvailabilityOverviewDay,
  AvailabilityOverviewSlotState,
  AvailabilityOverviewTeamMember,
  AvailabilityOverviewUnavailableReason,
  ShowingStatus
} from "api/scheduling/types";
import { showingStatusConfig } from "views/showings/constants";

interface WeekDay {
  /** "yyyy-MM-dd" */
  date: string;
  weekdayLabel: string;
  dayNumber: string;
}

/** Build the 7 Sun→Sat days of the week anchored on `weekSunday`. */
export const buildWeekDays = (weekSunday: DateTime): WeekDay[] =>
  Array.from({ length: 7 }, (_, i) => {
    const dt = weekSunday.plus({ days: i });
    return {
      date: dt.toFormat("yyyy-MM-dd"),
      weekdayLabel: dt.toFormat("ccc"),
      dayNumber: dt.toFormat("d")
    };
  });

/** Human label for the week selector, e.g. "Jun 14 - Jun 20". */
export const formatWeekLabel = (weekSunday: DateTime): string =>
  `${weekSunday.toFormat("MMM d")} - ${weekSunday
    .plus({ days: 6 })
    .toFormat("MMM d")}`;

/** Find a team member's day payload, if the backend emitted one. */
export const findDay = (
  member: AvailabilityOverviewTeamMember,
  date: string
): AvailabilityOverviewDay | undefined =>
  member.days.find((d) => d.date === date);

const minutesFromBandStart = (dt: DateTime, bandStartHour: number): number =>
  (dt.hour - bandStartHour) * 60 + dt.minute;

/** Minute offset from band start → horizontal px, at the measured hour width. */
export const minutesToPx = (minutes: number, hourPx: number): number =>
  (minutes / 60) * hourPx;

interface DayRegion {
  state: AvailabilityOverviewSlotState;
  reason: AvailabilityOverviewUnavailableReason | null;
  /** minutes from the band start */
  topMinutes: number;
  durationMinutes: number;
  /** Preformatted "h:mm a" bounds for the tooltip; empty when allDay. */
  startLabel: string;
  endLabel: string;
  /** Unavailable region covering the entire visible band. */
  allDay: boolean;
}

/**
 * Merge the day's slots into contiguous same-state/same-reason regions for the
 * timeline: solid green fills for available runs, hatched blocks (with a
 * tooltip range + reason) for unavailable runs. Any band time the backend did
 * not emit a slot for — a missing day, or the band tail when the slot
 * duration does not divide the band length — is padded with unavailable
 * regions per the locked contract (unemitted ⇒ unavailable).
 */
export const buildDayRegions = (
  day: AvailabilityOverviewDay | undefined,
  timezone: string,
  bandStartHour: number,
  bandEndHour: number
): DayRegion[] => {
  const bandMinutes = (bandEndHour - bandStartHour) * 60;

  const minuteLabel = (minutes: number): string =>
    DateTime.fromObject({
      hour: (bandStartHour + Math.floor(minutes / 60)) % 24,
      minute: minutes % 60
    }).toFormat("h:mm a");

  const gapRegion = (from: number, to: number): DayRegion => ({
    state: "unavailable",
    reason: null,
    topMinutes: from,
    durationMinutes: to - from,
    startLabel: minuteLabel(from),
    endLabel: minuteLabel(to),
    allDay: false
  });

  const sorted = (day?.slots ?? [])
    .map((slot) => {
      const start = DateTime.fromISO(slot.start_time).setZone(timezone);
      const end = DateTime.fromISO(slot.end_time).setZone(timezone);
      const startMin = minutesFromBandStart(start, bandStartHour);
      // Duration from the real instant difference, not wall-clock math —
      // wall clocks repeat on DST fall-back and jump at midnight.
      const durationMin = Math.max(0, end.diff(start, "minutes").minutes);
      return {
        state: slot.state,
        reason: slot.reason,
        startMin,
        endMin: startMin + durationMin,
        startLabel: formatTime(slot.start_time, timezone),
        endLabel: formatTime(slot.end_time, timezone)
      };
    })
    .sort((a, b) => a.startMin - b.startMin);

  const merged: DayRegion[] = [];
  sorted.forEach((slot) => {
    const last = merged[merged.length - 1];
    const lastEnd = last ? last.topMinutes + last.durationMinutes : null;

    if (
      last &&
      last.state === slot.state &&
      last.reason === slot.reason &&
      lastEnd === slot.startMin
    ) {
      last.durationMinutes = slot.endMin - last.topMinutes;
      last.endLabel = slot.endLabel;
      return;
    }
    merged.push({
      state: slot.state,
      reason: slot.reason,
      topMinutes: slot.startMin,
      durationMinutes: slot.endMin - slot.startMin,
      startLabel: slot.startLabel,
      endLabel: slot.endLabel,
      allDay: false
    });
  });

  // Pad unemitted band time (missing day, band tail, DST gaps) so no region
  // of the axis renders as a blank third state.
  const padded: DayRegion[] = [];
  let cursor = 0;
  merged.forEach((region) => {
    if (region.topMinutes > cursor)
      padded.push(gapRegion(cursor, region.topMinutes));

    padded.push(region);
    cursor = Math.max(cursor, region.topMinutes + region.durationMinutes);
  });
  if (cursor < bandMinutes) padded.push(gapRegion(cursor, bandMinutes));

  return padded.map((region) => ({
    ...region,
    allDay:
      region.state === "unavailable" &&
      region.topMinutes <= 0 &&
      region.durationMinutes >= bandMinutes
  }));
};

interface ShowingGeometry {
  leftPx: number;
  widthPx: number;
}

/**
 * Clamped horizontal placement of a showing card within the timeline band.
 * Showings can be scheduled at any time of day (manual scheduling allows
 * 00:00–23:45), so out-of-band portions are clipped to the band edges and a
 * showing entirely outside the band returns null (not rendered).
 */
export const showingGeometry = (
  startIso: string,
  endIso: string,
  timezone: string,
  bandStartHour: number,
  bandEndHour: number,
  hourPx: number
): ShowingGeometry | null => {
  const bandMinutes = (bandEndHour - bandStartHour) * 60;
  const start = DateTime.fromISO(startIso).setZone(timezone);
  const end = DateTime.fromISO(endIso).setZone(timezone);
  const startMin = minutesFromBandStart(start, bandStartHour);
  // Real instant difference — see buildDayRegions.
  const durationMin = Math.max(0, end.diff(start, "minutes").minutes);

  const left = Math.max(0, startMin);
  const right = Math.min(bandMinutes, startMin + durationMin);
  if (right <= left) return null;

  return {
    leftPx: minutesToPx(left, hourPx),
    widthPx: minutesToPx(right - left, hourPx)
  };
};

/** Time-of-day label in the response timezone, e.g. "3:00 PM". */
export const formatTime = (iso: string, timezone: string): string =>
  DateTime.fromISO(iso).setZone(timezone).toFormat("h:mm a");

/** Hour-axis label, e.g. "9 AM" (hour 24 renders as 12 AM). */
export const formatHourLabel = (hour: number): string =>
  DateTime.fromObject({ hour: hour % 24 }).toFormat("h a");

interface ShowingDisplay {
  label: string;
  bgClass: string;
  textClass: string;
  dotClass: string;
}

const DEFAULT_SHOWING_DISPLAY: ShowingDisplay = {
  label: "Scheduled showing",
  bgClass: showingStatusConfig[ShowingStatus.Confirmed].bgClass,
  textClass: showingStatusConfig[ShowingStatus.Confirmed].textClass,
  dotClass: showingStatusConfig[ShowingStatus.Confirmed].dotClass
};

/**
 * Map an opaque showing status string to display config, reusing the
 * views/showings color palette. Falls back to the "Scheduled" treatment for
 * unknown statuses (status is treated as opaque text per the contract).
 */
export const getShowingDisplay = (status: string): ShowingDisplay => {
  const config = showingStatusConfig[status as ShowingStatus];
  if (!config) return DEFAULT_SHOWING_DISPLAY;
  return {
    label: config.label,
    bgClass: config.bgClass,
    textClass: config.textClass,
    dotClass: config.dotClass
  };
};
